import Vue from 'vue';

import axios from 'axios';
import * as _ from 'lodash';
import fileDownload from 'js-file-download';
import {
 get, post, postWithParams, put,
} from '../../../../request';

function setCache(key, val) {
  if (!val) return localStorage.removeItem(`wowzi:${key}`);
  return localStorage.setItem(
    `wowzi:${key}`,
    JSON.stringify(val),
  );
}

function getCache(key) {
  return JSON.parse(
    localStorage.getItem(`wowzi:${key}`),
  );
}

async function uploadImages(imgArr, ref, id, url) {
  const promises = [];
  imgArr.forEach((img) => {
    const formData = new FormData();
    formData.append(ref, id);
    formData.append('file', img);
    promises.push(
      axios.post(url, formData, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      }),
    );
  });
  await Promise.all(promises);
}

function mapLevels(data) {
  data.influencerLevels = {};
  const influencerLevels = data.channels_active[1].levels;
  influencerLevels.forEach((elem) => {
    const name = elem.label.split(' ')[0];
    let elemMap = { ...elem };
    if (elem.value !== 'META') {
      elemMap = { ...elem, label: elem.label.split(' ')[1] };
    }
    if (!(data.influencerLevels[name])) {
      data.influencerLevels[name] = [];
      data.influencerLevels[name].push(elemMap);
    } else {
      data.influencerLevels[name].push(elemMap);
    }
  });
}

const state = () => ({
  campaignsLoading: false,
  campaigns: [],
  influencers: [],
  error: null,
  isOldUser: getCache('old_user') || false,
  stats: null,
  config: null,
  incomeCategoriesByCountry: null,
  currentCurrency: null,
  budgetMinMax: null,
  totalInfluencers: 0,
  groups: [],
  contentCreatorTypes: [],
  topPosts: [],
  allPosts: [],
  activeCountries: [],
  reports: [],
});

const getters = {
  isOldUser: vState => vState.isOldUser,
};

const mutations = {
  error(s, e) {
    Vue.set(s, 'error', e);
  },
  setCampaignsLoading(s, data) {
    Vue.set(s, 'campaignsLoading', data);
  },
  setOldUser(s, isCache) {
    Vue.set(s, 'isOldUser', isCache);
    setCache('old_user', isCache);
  },
  setConfig(s, data) {
    Vue.set(s, 'config', data);
  },
  setIncomeCategoriesByCountry(s, data) {
    Vue.set(s, 'incomeCategoriesByCountry', data);
  },
  setCurrentCurrencyByCountry(s, data) {
    Vue.set(s, 'currentCurrency', data);
  },
  setBudgetMinMaxByCurrency(s, data) {
    Vue.set(s, 'budgetMinMax', data);
  },
  setCampaigns(s, data) {
    Vue.set(s, 'campaigns', data);
  },
  setCampaign(s, campaignForm) {
    campaignForm.status = 'Upcoming';
    s.campaigns.push(campaignForm);
  },
  setStats(s, stats) {
    Vue.set(s, 'stats', stats);
  },
  setGroups(s, data) {
    Vue.set(s, 'groups', data);
  },
  setActiveCountries(s, data) {
    Vue.set(s, 'activeCountries', data);
  },
  updateCampaign(s, campaignForm) {
    const idx = s.campaigns.findIndex(x => x.id === campaignForm.id);
    if (idx > -1) {
      const obj = Object.assign({}, s.campaigns[idx], campaignForm);
      s.campaigns.splice(idx, 1, obj);
    }
  },

  setInfluencers(s, influencers) {
    Vue.set(s, 'influencers', influencers);
  },

  setTotalInfluencers(s, count) {
    Vue.set(s, 'totalInfluencers', count);
  },
  deleteCampaign(s, campaignForm) {
    const idx = s.campaigns.findIndex(x => x.id === campaignForm.id);
    if (idx > -1) {
      s.campaigns.splice(idx, 1);
    }
  },

  setTopPosts(s, data) {
    Vue.set(s, 'topPosts', data);
  },

  setAllPosts(s, data) {
    Vue.set(s, 'allPosts', data);
  },

  setReports(s, data) {
    Vue.set(s, 'reports', data);
  },

  setContentCreatorTypes(s, data) {
    Vue.set(s, 'contentCreatorTypes', data);
  },
};

const actions = {
  async doInitialize({ commit }) {
    const data = await get('/conf/all', { commit });
    data.subcategories = [];
    data.categories.forEach((cat) => {
      data.subcategories = _.concat(data.subcategories, cat.subcategories);
    });
    mapLevels(data);
    const json = data;
    commit('setConfig', json);
  },

  async doGetIncomeLevelsByCountry({ commit }, country) {
    const data = await get(`/conf/income_categories/${country}`, { commit });
    commit('setIncomeCategoriesByCountry', data);
  },

  async doGetCurrentCurrencyByCountry({ commit }, country) {
    const data = await get(`/conf/currencies/countries/${country}`, { commit });
    commit('setCurrentCurrencyByCountry', data);
  },

  async doGetBudgetMinMaxByCurrency({ commit }, currency) {
    const data = await get(`/conf/budget_min_max/${currency}`, { commit });
    commit('setBudgetMinMaxByCurrency', data);
  },

  async doGetGroups({ commit }, form) {
    try {
      const groups = await get('/merchant/influencer_groups', form, { commit });
      commit('setGroups', groups);
    } catch (e) {
      console.info(e);
    }
  },

  async doGetCampaigns({ commit }, companyId) {
    commit('setCampaignsLoading', true);
    commit('error', null);
    try {
      const data = await get(`/campaigns/all/companies/${companyId}`, { commit });

      // eslint-disable-next-line camelcase
      const { ongoing_campaigns, upcoming_campaigns, finished_campaigns } = data;
      const finished = finished_campaigns.map(c => ({ ...c, status: 'Completed' }));
      const ongoing = ongoing_campaigns.map(c => ({ ...c, status: 'Ongoing' }));
      const upcoming = upcoming_campaigns.map(c => ({ ...c, status: 'Upcoming' }));
      const campaignArr = [...ongoing, ...upcoming, ...finished];
      commit('setCampaigns', campaignArr);
      commit('setCampaignsLoading', false);
    } catch (e) {
      commit('error', e);
      commit('setCampaignsLoading', false);
    }
  },

  async doGetStats({ commit }) {
    commit('error', null);
    try {
      const data = await get('/campaigns/dashboard', { commit });
      commit('setStats', data);
    } catch (e) {
      commit('error', e);
    }
  },

  async doGetCampaign({ commit }, { campaignId }) {
    commit('error', null);
    try {
      const res = await get(`/campaigns/${campaignId}`, { commit });
      const data = await get(`/campaigns/${campaignId}/stats`, { commit });
      delete data.campaign;
      const obj = Object.assign({}, res, data);
      commit('updateCampaign', obj);
    } catch (e) {
      commit('error', e);
    }
  },

  async doRunCampaign({ commit }, form) {
    const campaignForm = JSON.parse(JSON.stringify(form));

    const { group } = form;
    if (group) {
      campaignForm.influencer_groups = [group.id];
    }
    delete campaignForm.group;

    const { locations } = form.preferences;
    if (locations) {
      campaignForm.preferences.locations = locations.map(x => x.value);
    }

    const { countries } = form.preferences;
    if (countries) {
      campaignForm.preferences.countries = countries.map(x => x.value);
    }

    const { files } = form;

    const taskFiles = [];
    delete campaignForm.files;
    delete campaignForm.category;
    if (form.tasks.length > 0) {
      form.tasks.forEach((elem, index) => {
        campaignForm.tasks[index].must_haves = campaignForm.tasks[index].must_haves
          .filter(el => el);
        campaignForm.tasks[index].should_not_haves = campaignForm.tasks[index].should_not_haves
          .filter(el => el);
        if (elem.files && elem.files.length > 0) {
          const obj = {
            id: null,
            channel: elem.channel,
            files: elem.files,
          };
          taskFiles.push(obj);
          delete campaignForm.tasks[index].files;
        } else {
          delete campaignForm.tasks[index].files;
        }
      });
    }
    try {
      const data = await post('/campaigns', campaignForm, { commit });
      commit('setCampaign', data);
      if (files && files.length > 0) {
        uploadImages(files, 'campaign_id', data.id, '/upload/campaign/logo');
      }
      if (taskFiles && taskFiles.length > 0) {
        taskFiles.forEach((elem) => {
          const task = data.tasks.find(x => x.channel === elem.channel);
          elem.id = task.id;
          uploadImages(elem.files, 'task_id', elem.id, '/upload/campaign/task');
        });
      }
    } catch (e) {
      commit('error', e);
      throw e;
    }
  },

  async doCancelCampaign({ commit }, { campaignId }) {
    commit('error', null);
    try {
      const data = await put(`/campaigns/${campaignId}/cancel`, null, { commit });
      commit('deleteCampaign', data);
    } catch (e) {
      commit('error', e);
    }
  },

  async doGetInfluencers({ commit }, form) {
    commit('error', null);
    try {
      const dataCount = await post('/influencers/filter/count', form, { commit });
      const data = await post('/influencers/filter/random', form, { commit });
      commit('setTotalInfluencers', dataCount.count);
      commit('setInfluencers', data);
    } catch (e) {
      commit('error', e);
    }
  },

  async doGetPayments({ commit }, id) {
    const headers = {
      accept: 'application/pdf',
    };
    const payment = await get(`/payments/merchant/receipt/pdf/${id}`, { commit, headers, responseType: 'blob' });
    return payment;
  },

  async doGetDetailedCampaignStats({ commit }, form) {
    const stats = await post(`/campaigns/${form.id}/statistics`, {
      channels: form.channels,
    }, { commit });
    return stats;
  },

  async getTopPosts({ commit }, id) {
    const data = await get(`/campaigns/${id}/top_posts`, { commit });
    commit('setTopPosts', data);
  },

  async getAllPosts({ commit }, id) {
    const data = await postWithParams(`/campaigns/${id}/all_posts`, { channels: ['TWITTER', 'FACEBOOK', 'INSTAGRAM', 'LINKEDIN', 'TIKTOK'] }, { page_number: 1, page_size: 50 }, { commit });
    commit('setAllPosts', data);
  },

  async doGetActiveCountries({ commit }) {
    const data = await get('/conf/countries/active', { commit });
    commit('setActiveCountries', data);
  },

  async getCities({ commit }, countryCode) {
    const data = get(`/conf/countries/${countryCode}/locations`, { commit });
    return data;
  },

  async deGetReportsForMerchant({ commit }) {
    const response = await get('/reports/reports', { commit });
    commit('setReports', response);
  },

  async doDownloadReportForCampaign({ commit }, data) {
    const response = await get(`/reports/${data.report_name}/campaigns/${data.campaign_id}/download`, { commit, responseType: 'blob' });
    fileDownload(response, data.report_name);
  },

  async doGetContentCreatorTypes({ commit }) {
    try {
      const data = await get('/content-creator-types', { commit });
      commit('setContentCreatorTypes', data);
    } catch (e) {
      commit('error', e);
    }
  },
};

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
};
